// Map layer of the Striping Plan tab (spec 2026-10-14 Part B): the route's lanes
// highlighted, the list as numbered draggable markers, click-to-add in Add mode, and
// zoom-to from the side panel. Imperative Google Maps objects, diffed by point id.
// Must render inside <GoogleMap> (useGoogleMap).
import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';
import {
  STRIPING_ROUTE_COLOR, stripingMarkerDescriptor, toGoogleMarkerOptions,
} from './stripingMarkers';

const DOUBLE_CLICK_GUARD_MS = 400; // the second click of a double-click adds nothing
const DRAG_CLICK_GUARD_MS = 180; // a drag can end in a click (PathSelectorMap does the same)
const FOCUS_ZOOM = 19;

export default function StripingMapLayer({ editor }) {
  const map = useGoogleMap();
  const markersRef = useRef(new Map());
  const mountedAt = useRef(Date.now()); // ignore a focus left over from before the tab opened
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // The selected route's lanes, highlighted.
  useEffect(() => {
    const g = window.google?.maps;
    if (!map || !g) return undefined;
    const lines = (editor.laneLines || []).map(
      (path) => new g.Polyline({
        map, path, clickable: false, zIndex: 5,
        strokeColor: STRIPING_ROUTE_COLOR, strokeOpacity: 0.85, strokeWeight: 7,
      }),
    );
    return () => lines.forEach((l) => l.setMap(null));
  }, [map, editor.laneLines]);

  // Markers: create, update or remove by point id.
  useEffect(() => {
    const g = window.google?.maps;
    if (!map || !g) return;
    const seen = new Set();
    for (const row of editor.rows) {
      seen.add(row.id);
      const options = toGoogleMarkerOptions(
        stripingMarkerDescriptor(row, { selected: row.id === editor.selectedPointId }), g,
      );
      const position = { lat: row.lat, lng: row.lng };
      const entry = markersRef.current.get(row.id);
      if (entry) {
        entry.marker.setPosition(position);
        entry.marker.setOptions(options);
        continue;
      }
      const marker = new g.Marker({ map, position, draggable: true, ...options });
      const listeners = [
        marker.addListener('click', () => editorRef.current.selectPoint(row.id)),
        marker.addListener('dragend', () => {
          const p = marker.getPosition();
          if (p) editorRef.current.movePoint(row.id, p.lat(), p.lng());
        }),
      ];
      markersRef.current.set(row.id, { marker, listeners });
    }
    for (const [id, entry] of markersRef.current) {
      if (seen.has(id)) continue;
      entry.listeners.forEach((l) => l.remove());
      entry.marker.setMap(null);
      markersRef.current.delete(id);
    }
  }, [map, editor.rows, editor.selectedPointId]);

  // Remove every marker when the tab closes.
  useEffect(() => () => {
    for (const entry of markersRef.current.values()) {
      entry.listeners.forEach((l) => l.remove());
      entry.marker.setMap(null);
    }
    markersRef.current.clear();
  }, []);

  // Add mode: a map click adds a point (not the trailing click of a drag).
  useEffect(() => {
    if (!map || !editor.addMode) return undefined;
    let dragging = false;
    let lastDragEnd = 0;
    let lastAddAt = 0;
    const listeners = [
      map.addListener('dragstart', () => { dragging = true; }),
      map.addListener('dragend', () => { dragging = false; lastDragEnd = Date.now(); }),
      map.addListener('click', (e) => {
        if (dragging || Date.now() - lastDragEnd < DRAG_CLICK_GUARD_MS) return;
        // A double-click delivers two clicks (and zooms): add one point, not two.
        if (Date.now() - lastAddAt < DOUBLE_CLICK_GUARD_MS) return;
        const lat = e?.latLng?.lat();
        const lng = e?.latLng?.lng();
        if (lat != null && lng != null) {
          lastAddAt = Date.now();
          editorRef.current.addPoint(lat, lng);
        }
      }),
    ];
    return () => {
      listeners.forEach((l) => l.remove());
    };
  }, [map, editor.addMode]);

  // Zoom to a point picked in the side panel.
  useEffect(() => {
    if (!map || !editor.focus || editor.focus.nonce < mountedAt.current) return;
    map.panTo({ lat: editor.focus.lat, lng: editor.focus.lng });
    if ((map.getZoom() ?? 0) < FOCUS_ZOOM) map.setZoom(FOCUS_ZOOM);
  }, [map, editor.focus]);

  return null;
}
